import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Modal, Tooltip } from 'antd';

import { Cross, Download } from 'icons';
import { useModal } from 'hooks/use-modal';
import { environment } from 'environments';

import styles from './Attachment.module.scss';

export interface AttachmentProps {
  fileName: string;
  handleRemoveAttachment?: (fileName: string, attachmentId?: string, file?: File) => void;
  requestId?: string | number;
  attachmentId?: string;
  file?: File;
  isEdit?: boolean;
  isAttachmentsExpired?: boolean;
  isInvalid?: boolean;
  fileError?: string;
}

export const Attachment = ({
  fileName,
  handleRemoveAttachment,
  requestId,
  attachmentId,
  file,
  isEdit,
  isAttachmentsExpired,
  isInvalid,
  fileError,
}: AttachmentProps) => {
  const onClick = useCallback(
    () => handleRemoveAttachment?.(fileName, attachmentId, file),
    [handleRemoveAttachment, fileName, attachmentId, file],
  );
  const { isOpenModal, openModal, closeModal } = useModal();
  const [fileSrc, setFileSrc] = useState<string>();

  useEffect(() => {
    if (isAttachmentsExpired) {
      return;
    }

    async function fetchImage() {
      const response = await axios.get(
        `${environment.baseApiUrl}Users/requests/${requestId}/attachments/${attachmentId}`,
        {
          responseType: 'blob',
        },
      );

      setFileSrc(URL.createObjectURL(response.data));
    }

    if (file && typeof File !== 'undefined' && file instanceof File) {
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setFileSrc(reader.result as string);
      };
    } else if (requestId && attachmentId) {
      fetchImage();
    }
  }, [file, requestId, attachmentId, isAttachmentsExpired]);

  const isImage = useMemo(() => /\.(png|jpg|jpeg)/.test(fileName), [fileName]);

  return (
    <>
      <div>
        <div className={styles.attachment}>
          {isImage && fileSrc && (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <img className={styles.inlinePreviewImage} src={fileSrc} alt={fileName} onClick={openModal} />
          )}
          <div className={styles.nameBlock}>
            <Tooltip title={fileName} placement="topLeft">
              <span className={`${styles.fileName} ${isInvalid ? styles.invalidFileName : ''}`.trim()}>{fileName}</span>
            </Tooltip>
          </div>

          <div className={styles.actions}>
            <a href={fileSrc} download={fileName}>
              <Download />
            </a>
            {isEdit && <Cross onClick={onClick} />}
          </div>
        </div>
        {fileError && <div className={styles.fileError}>{fileError}</div>}
      </div>

      <Modal open={isOpenModal} onCancel={closeModal} title={fileName} footer={null}>
        {fileSrc && <img className={styles.modalPreviewImage} src={fileSrc} alt={fileName} />}
      </Modal>
    </>
  );
};
